import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { GearSix, BellRinging } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const NOTIF_CATEGORIES = [
  ["timeoff", "Time-off approvals"],
  ["sick_notes", "Doctor's notes"],
  ["milestones", "Milestones"],
  ["reviews", "Performance reviews"],
  ["licensing", "State licensing"],
];
const CATEGORY_PERM = {
  timeoff: "timeoff.approve",
  sick_notes: "sicknotes.review",
  milestones: "users.manage",
  reviews: "reviews.complete",
  licensing: "licenses.review",
};
const canDo = (u, perm) => u.role === "super_admin" || (u.permission_overrides || {})[perm] === true;
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "UTC"];

export default function SettingsPage() {
  const { hasPerm } = useAuth();
  const [settings, setSettings] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [remindersResult, setRemindersResult] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/users").then((r) => setAdmins(r.data.filter((u) => u.is_active !== false && (u.role === "super_admin" || Object.values(u.permission_overrides || {}).some(Boolean))))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!hasPerm("settings.manage") && !hasPerm("users.manage")) return <Navigate to="/" replace />;

  const toggleAssignee = (cat, uid) => {
    const na = { ...(settings.notification_assignees || {}) };
    const cur = na[cat] || [];
    na[cat] = cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid];
    setSettings({ ...settings, notification_assignees: na });
  };

  const savePolicySettings = async () => {
    try {
      await api.put("/settings", {
        max_per_department: parseInt(settings.max_per_department),
        max_company_wide: parseInt(settings.max_company_wide),
        advance_notice_days: parseInt(settings.advance_notice_days),
        sick_annual_hours: parseFloat(settings.sick_annual_hours),
      });
      toast.success("Policy settings saved — logged to audit trail");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const saveNotifSettings = async () => {
    try {
      await api.put("/settings", {
        business_hours_start: settings.business_hours_start,
        business_hours_end: settings.business_hours_end,
        business_timezone: settings.business_timezone,
        reminder_escalation_business_days: parseInt(settings.reminder_escalation_business_days) || 3,
        short_notice_reminder_days: parseInt(settings.short_notice_reminder_days) || 1,
        notification_assignees: settings.notification_assignees || {},
      });
      toast.success("Notification settings saved — logged to audit trail");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const runReminders = async () => {
    setBusy("reminders");
    try {
      const { data } = await api.post("/settings/run-reminders");
      setRemindersResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to run reminders");
    } finally {
      setBusy("");
    }
  };

  if (!settings) return <p className="text-sm text-muted-foreground">Loading settings…</p>;

  return (
    <div className="space-y-8">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Configuration</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
          <GearSix size={30} weight="duotone" className="text-indigo-600" /> Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Company-wide time-off policy and notification routing. Every change is recorded in the audit trail.</p>
      </div>

      <div className="bg-card border border-border rounded-md p-6 space-y-4" data-testid="policy-settings-card">
        <h3 className="font-heading font-semibold tracking-tight">Time-off policy settings</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div><Label className="text-xs">Max off per department</Label><Input data-testid="setting-max-dept" type="number" className="mt-1.5" value={settings.max_per_department} onChange={(e) => setSettings({ ...settings, max_per_department: e.target.value })} /></div>
          <div><Label className="text-xs">Max off company-wide</Label><Input data-testid="setting-max-company" type="number" className="mt-1.5" value={settings.max_company_wide} onChange={(e) => setSettings({ ...settings, max_company_wide: e.target.value })} /></div>
          <div><Label className="text-xs">Advance notice (days)</Label><Input data-testid="setting-advance-days" type="number" className="mt-1.5" value={settings.advance_notice_days} onChange={(e) => setSettings({ ...settings, advance_notice_days: e.target.value })} /></div>
          <div><Label className="text-xs">Annual sick hours</Label><Input data-testid="setting-sick-hours" type="number" className="mt-1.5" value={settings.sick_annual_hours} onChange={(e) => setSettings({ ...settings, sick_annual_hours: e.target.value })} /></div>
        </div>
        <Button data-testid="save-settings-btn" onClick={savePolicySettings} className="bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">Save policy settings</Button>
      </div>

      <div className="bg-card border border-border rounded-md p-6 space-y-5" data-testid="notification-settings-card">
        <div className="flex items-center gap-2">
          <BellRinging size={20} weight="duotone" className="text-indigo-600" />
          <h3 className="font-heading font-semibold tracking-tight">Notification routing & reminders</h3>
        </div>
        <p className="text-xs text-muted-foreground -mt-3">Pick who gets emailed for each category. They receive an immediate notification plus a <strong>daily reminder during business hours until action is taken</strong>. If nobody is assigned, all super admins are notified. Items pending longer than the escalation threshold also go to every super admin.</p>
        <div className="space-y-3">
          {NOTIF_CATEGORIES.map(([cat, label]) => (
            <div key={cat} className="flex flex-wrap items-center gap-2" data-testid={`notif-cat-${cat}`}>
              <span className="text-sm font-medium w-44 shrink-0">{label}</span>
              {admins.filter((a) => canDo(a, CATEGORY_PERM[cat])).map((a) => {
                const selected = ((settings.notification_assignees || {})[cat] || []).includes(a.id);
                return (
                  <button key={a.id} data-testid={`notif-${cat}-${a.id}`} onClick={() => toggleAssignee(cat, a.id)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${selected ? "bg-indigo-600 border-indigo-600 text-white" : "bg-card border-border text-muted-foreground hover:border-indigo-400"}`}>
                    {a.full_name}
                  </button>
                );
              })}
              {((settings.notification_assignees || {})[cat] || []).length === 0 && <Badge className="bg-slate-100 text-slate-600">defaults to super admins</Badge>}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div><Label className="text-xs">Business hours start</Label><Input data-testid="setting-bh-start" type="time" className="mt-1.5" value={settings.business_hours_start || "09:00"} onChange={(e) => setSettings({ ...settings, business_hours_start: e.target.value })} /></div>
          <div><Label className="text-xs">Business hours end</Label><Input data-testid="setting-bh-end" type="time" className="mt-1.5" value={settings.business_hours_end || "17:00"} onChange={(e) => setSettings({ ...settings, business_hours_end: e.target.value })} /></div>
          <div>
            <Label className="text-xs">Timezone</Label>
            <Select value={settings.business_timezone || "America/Chicago"} onValueChange={(v) => setSettings({ ...settings, business_timezone: v })}>
              <SelectTrigger data-testid="setting-timezone" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Escalate after (business days)</Label><Input data-testid="setting-escalation-days" type="number" min="1" className="mt-1.5" value={settings.reminder_escalation_business_days ?? 3} onChange={(e) => setSettings({ ...settings, reminder_escalation_business_days: e.target.value })} /></div>
          <div>
            <Label className="text-xs">Short-notice reminder every (business days)</Label>
            <Input data-testid="setting-short-notice-days" type="number" min="1" className="mt-1.5" value={settings.short_notice_reminder_days ?? 1} onChange={(e) => setSettings({ ...settings, short_notice_reminder_days: e.target.value })} />
            <p className="text-[11px] text-muted-foreground mt-1">While a short-notice PTO request is pending, the requester and time-off approvers are re-emailed at this cadence.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="save-notif-settings-btn" onClick={saveNotifSettings} className="bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">Save notification settings</Button>
          <Button data-testid="run-reminders-btn" variant="outline" onClick={runReminders} disabled={busy === "reminders"}>
            {busy === "reminders" ? "Running…" : "Run reminders now"}
          </Button>
        </div>
        {remindersResult && (
          <div className="text-xs space-y-1 text-muted-foreground" data-testid="reminders-result">
            <p>{remindersResult.note}</p>
            {remindersResult.sent?.map((s, i) => <p key={i}>• Sent to <strong>{s.recipient}</strong> ({s.email}) — {s.items} item(s)</p>)}
          </div>
        )}
      </div>
    </div>
  );
}
